import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { corporateRouteBySlug, corporateRouteToTabId } from '../config/corporateNav';
import { firstAccessibleCorporatePath, isCorporateTabEnabled } from '../utils/corporatePageAccess';
import { CorporateUseCaseScreenContent } from '../components/corporate/CorporateUseCaseScreens';
import { loadCorporateProfile } from '../utils/corporateProfileStorage';
import atherData from '../data/atherDataRequirements.json';

const STATIC_ALERTS =
  atherData.screens
    .find((s) => s.id === 'screen_1')
    ?.data_collections.find((c) => c.name === 'live_alert_feed')?.sample_data || [];

function getStaticBannerAlert() {
  return STATIC_ALERTS.find((a) => a.severity === 'RED') || STATIC_ALERTS[0] || null;
}

function bannerSeverityStyle(severity) {
  if (severity === 'RED') {
    return {
      wrap: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900/50',
      text: 'text-red-800 dark:text-red-200',
      btn: 'text-red-700 dark:text-red-300 border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/30',
      label: 'CRITICAL',
    };
  }
  if (severity === 'YELLOW') {
    return {
      wrap: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/50',
      text: 'text-amber-900 dark:text-amber-200',
      btn: 'text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30',
      label: 'HIGH IMPACT',
    };
  }
  return {
    wrap: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50',
    text: 'text-emerald-900 dark:text-emerald-200',
    btn: 'text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700',
    label: 'ALERT',
  };
}

const CorporateCommandCentre = () => {
  const { screenSlug } = useParams();
  const { user } = useAuth();
  const features = user?.subscription_plan_features || [];
  const routeMeta = corporateRouteBySlug(screenSlug || 'alerts');
  const tabId = corporateRouteToTabId(routeMeta.route);
  const profile = loadCorporateProfile();

  if (!isCorporateTabEnabled(tabId, features, user)) {
    return <Navigate to={firstAccessibleCorporatePath(features, user)} replace />;
  }

  const bannerAlert = getStaticBannerAlert();
  const bannerStyle = bannerSeverityStyle(bannerAlert?.severity);
  const showBanner = routeMeta.route === 'alerts' && bannerAlert;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-white transition-colors duration-200">
      {showBanner ? (
        <div
          className={`border-b-2 px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-3 ${bannerStyle.wrap}`}
        >
          <span className="relative flex h-2 w-2 flex-shrink-0 rounded-full bg-red-500">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          </span>
          <div className={`flex-1 min-w-0 text-xs font-mono ${bannerStyle.text}`}>
            <span className="font-bold">{bannerStyle.label}</span>
            {' — '}
            {bannerAlert.title}
            {bannerAlert.summary ? ` · ${bannerAlert.summary}` : ''}
            {bannerAlert.timestamp_ago ? (
              <span className="opacity-80">
                {' '}
                · {bannerAlert.timestamp_ago}
                {bannerAlert.source_name ? ` · ${bannerAlert.source_name}` : ''}
              </span>
            ) : null}
          </div>
          {bannerAlert.source_url ? (
            <a
              href={bannerAlert.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-xs font-mono px-3 py-1 rounded border whitespace-nowrap ${bannerStyle.btn}`}
            >
              Read article →
            </a>
          ) : null}
        </div>
      ) : null}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <CorporateUseCaseScreenContent tabId={tabId} profile={profile} />
      </div>
    </div>
  );
};

export default CorporateCommandCentre;
